/**
 * useFilterStore — Zustand store for graph filter state.
 *
 * Extracted from Workbench.tsx as part of Phase 0 full.
 * Owns: graphFilters, filterCounts, showFilterBar, followMoneyMode.
 *
 * The 200-line filter *effect* stays in Workbench (needs graphRef for Cytoscape API)
 * but reads from this store instead of local useState.
 */

import { create } from 'zustand';
import type { GraphFilters } from '../components/workbench/GraphFilterBar';

// Inlined to avoid circular runtime import (GraphFilterBar imports useFilterStore)
const EMPTY_FILTERS: GraphFilters = {
    dustThresholdUsd: null,
    dateFrom: '',
    dateTo: '',
    riskLevels: new Set(['low', 'medium', 'high', 'critical'] as const),
    entityTypes: new Set(),
    maxHops: null,
    hideExchanges: false,
    showOnlyHighRisk: false,
    showIntermediateNodes: true, // R-AUDIT: Default to true so counterparts stay visible
    showAllNodes: false,
    tierFilterChosen: false,
    showNotAssessed: true,
};

export interface FilterCounts {
    visibleEdges: number;
    totalEdges: number;
    visibleNodes: number;
    totalNodes: number;
    /** Wallets hidden specifically by the intermediate (Tier-3) filter. */
    hiddenByTier?: number;
}

export interface FilterState {
    graphFilters: GraphFilters;
    filterCounts: FilterCounts;
    showFilterBar: boolean;
    followMoneyMode: boolean;
    currencyMode: 'original' | 'fiat';
    /**
     * Hide dust transactions EVERYWHERE — the Node Inspector lists (Activity,
     * Counterparties) AND the canvas. ON by default; dust (sub-cent: $0.000001
     * TRX etc.) is overwhelmingly noise for forensic investigators.
     *
     * This is the single, binary dust control (R-SSOT-4, see
     * docs/FRONTEND_STATE_ARCHITECTURE.md). Both surfaces read THIS field and
     * apply the same shared `isDustValue()` floor, so toggling it once is
     * reflected on the canvas and in every Inspector list. (The separate
     * `graphFilters.dustThresholdUsd` snap-stop is a user-chosen minimum-value
     * graph filter that *widens* this — handled in the graph-filter cleanup step.)
     */
    hideDust: boolean;

    // ── Actions ──
    setGraphFilters: (filters: GraphFilters) => void;
    mergeGraphFilters: (partial: Partial<GraphFilters>) => void;
    resetFilters: () => void;
    setFilterCounts: (counts: FilterCounts) => void;
    setShowFilterBar: (show: boolean) => void;
    setFollowMoneyMode: (mode: boolean) => void;
    setCurrencyMode: (mode: 'original' | 'fiat') => void;
    setHideDust: (hide: boolean) => void;

    /** Apply a partial filter preset (e.g. from AutoInvestigateCard). */
    applyPreset: (partial: Partial<GraphFilters>) => void;

    /** Auto-show filter bar when nodeCount >= 20 (single-fire). */
    maybeAutoExpand: (nodeCount: number) => void;
}

// Track whether auto-expand has fired (module-scope, not in Zustand state)
let _filterBarAutoShown = false;

export const useFilterStore = create<FilterState>((set, get) => ({
    graphFilters: { ...EMPTY_FILTERS, riskLevels: new Set(['low', 'medium', 'high', 'critical']) },
    filterCounts: { visibleEdges: 0, totalEdges: 0, visibleNodes: 0, totalNodes: 0 },
    showFilterBar: false,
    followMoneyMode: false,
    currencyMode: 'original',
    hideDust: true,  // dust hidden by default everywhere — noise dominates forensic value

    setGraphFilters: (filters) => set({ graphFilters: filters }),

    mergeGraphFilters: (partial) => {
        const current = get().graphFilters;
        set({ graphFilters: { ...current, ...partial } });
    },

    resetFilters: () =>
        set({
            graphFilters: { ...EMPTY_FILTERS, riskLevels: new Set(['low', 'medium', 'high', 'critical']) },
        }),

    setFilterCounts: (counts) => set({ filterCounts: counts }),

    setShowFilterBar: (show) => set({ showFilterBar: show }),

    setFollowMoneyMode: (mode) => set({ followMoneyMode: mode }),

    setCurrencyMode: (mode) => set({ currencyMode: mode }),

    setHideDust: (hide) => set({ hideDust: hide }),

    applyPreset: (partial) => {
        const current = get().graphFilters;
        set({ graphFilters: { ...current, ...partial } });
    },

    maybeAutoExpand: (nodeCount) => {
        if (nodeCount >= 20 && !_filterBarAutoShown && !get().showFilterBar) {
            _filterBarAutoShown = true;
            set({ showFilterBar: true });
        }
    },
}));

/** Reset the auto-expand guard (call when loading a new graph). */
export function resetFilterBarAutoShown() {
    _filterBarAutoShown = false;
}

// Register cross-store reset callback (avoids circular import)
import { registerNodeChangeCallback } from './useSelectionStore';
registerNodeChangeCallback(() => useFilterStore.getState().setFollowMoneyMode(false));

// Expose store on window for E2E test introspection. Mirrors the
// `window.__cy`, `__useGraphStore`, and `__useSelectionStore` patterns.
if (typeof window !== 'undefined') {
    (window as any).__useFilterStore = useFilterStore;
}
